import * as React from 'react';
import {useHistory} from 'src/rerouter';
import {createPortal} from 'react-dom';
import {create} from 'zustand';


export type PortalToAskAiProps = {
  id?: string;
  children: React.ReactNode;
};

/**
 * Tracks how many `PortalToAskAi` instances are actively portaling content
 * into the fullscreen target. The fullscreen shell reads this to decide its
 * layout: 0 → center the conversation, >0 → two-pane (conversation + asset).
 */
export const useFullscreenPortalStore = create<{
  count: number;
  increment: () => void;
  decrement: () => void;
}>((set) => ({
  count: 0,
  increment: () => set((s) => ({count: s.count + 1})),
  decrement: () => set((s) => ({count: Math.max(0, s.count - 1)})),
}));

export const PortalToAskAi = ({children}: PortalToAskAiProps) => {
  const history = useHistory();
  const isFullscreen = history.location.query?.fscr === 'true';
  const [target, setTarget] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!isFullscreen || typeof document === 'undefined') {
      setTarget(null);
      return;
    }
    const query = `[data-askai-fullscreen-portal-target="askai-fullscreen-portal"]`;
    const find = () => {
      setTarget(document.querySelector(query) as HTMLElement | null);
    };

    find();
    const observer = new MutationObserver(find);
    observer.observe(document.body, {childList: true, subtree: true});
    return () => observer.disconnect();
  }, [isFullscreen]);

  // Signal the fullscreen shell that an asset view is occupying the portal so
  // it switches from the centered conversation to the two-pane layout.
  const isPortaling = isFullscreen && !!target;
  React.useEffect(() => {
    if (!isPortaling) {
      return;
    }
    const {increment, decrement} = useFullscreenPortalStore.getState();
    increment();
    return () => decrement();
  }, [isPortaling]);

  if (!isFullscreen) {
    return <>{children}</>;
  }
  if (!target) {
    return null;
  }
  return createPortal(children, target);
};
